/* eslint-disable @typescript-eslint/ban-ts-comment */
import { subMinutes, isAfter } from 'date-fns'
import { customAlphabet } from 'nanoid'
import { SPACE } from '../../../constants'
import { PKCEGenerationError } from '../../../errors'
import { CODE_CHALLENGE_METHOD, PKCEPair } from '../constants/types'

// @ts-ignore
export const crypto = (typeof window !== 'undefined') ? (window.crypto || window.msCrypto) : globalThis.crypto ?? null

// @ts-ignore
export const subtle = crypto ? (crypto.subtle || crypto.webkitSubtle) : null

export function generatePrivateMembers<T>(list: Array<string>): T {
  return list.reduce((props, propName) => {
    props[propName] = Symbol(`#${propName}`)
    return props
  }, Object.create(null)) as T
}

export function wait(delay: number): Promise<void> {
  return new Promise(resolve => {
    setTimeout(resolve, delay)
  })
}

export function isTokenExpired(expiredAt: Date): boolean {
  const safeExpirationDate = subMinutes(expiredAt, 2)
  return isAfter(new Date(), safeExpirationDate)
}

export function parseJWT<T>(token: string): T | null {
  try {
    if (typeof token !== 'string') return null
    const parts = token.split('.')

    if (parts.length !== 3) {
      throw new Error('Invalid JWT format')
    }

    const [, data] = parts
    return JSON.parse(window.atob(data || SPACE))
  } catch (e) {
    return null
  }
}

/** * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 *              OIDC/Crypto Generators
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

export async function sha256(string: string): Promise<Uint8Array | ArrayBuffer> {
  if (!subtle) throw Error('Missing Crypto')
  return await subtle?.digest('SHA-256', encode(string))
}

export async function generateCodeVerifier() {
  return customAlphabet('0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz-_~.', 43)()
}

export async function generateNonce(size = 32) {
  return customAlphabet('0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz', size)()
}

export async function generateCodeChallenge(codeVerifier: string) {
  const hashHex = await sha256(codeVerifier)
  return bufferToBase64UrlEncoded(hashHex)
}

export async function generatePKCE(): Promise<PKCEPair> {
  try {
    const codeVerifier = await generateCodeVerifier()
    const codeChallenge = await generateCodeChallenge(codeVerifier)

    return {
      code_challenge: codeChallenge,
      code_verifier: codeVerifier,
      code_challenge_method: CODE_CHALLENGE_METHOD.SHA256
    }
  } catch (error) {
    throw new PKCEGenerationError(error.message)
  }
}

/** * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 *              ArrayBuffer ↔ String
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

export function arrayBufferToString(buffer: ArrayBuffer | SharedArrayBuffer | ArrayLike<number>) {
  const charCode = new Uint8Array(buffer)
  return String.fromCharCode.apply(null, charCode as unknown as number[])
}

export function stringToArrayBuffer(string: string): ArrayBuffer {
  const buffer = new ArrayBuffer(string.length)
  const array = new Uint8Array(buffer)

  for (let i = 0, length = string.length; i < length; i++) {
    array[i] = string.charCodeAt(i)
  }

  return buffer
}


/** * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 *                  Encode ↔ Decode
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * **/

export function encode(message: string): ArrayBuffer {
  if (!window.TextEncoder) return new Uint8Array(stringToArrayBuffer(message))
  return new TextEncoder().encode(message)
}

export function decode(bytes: ArrayBuffer): string {
  //@ts-ignore
  if (!window.TextDecoder) return String.fromCharCode.apply(null, new Uint8Array(bytes))
  return new TextDecoder('utf-8').decode(bytes)
}

/** * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 *                  UrlEncode ↔ UrlDecode
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * **/

export const urlEncodeB64 = (input: string) => {
  const b64Chars: { [index: string]: string } = { '+': '-', '/': '_', '=': SPACE }
  return input.replace(/[+/=]/g, (m: string) => b64Chars[m])
}

export const bufferToBase64UrlEncoded = (input: number[] | Uint8Array | ArrayBuffer): string => {
  const bytes = new Uint8Array(input)
  return urlEncodeB64(window.btoa(String.fromCharCode(...Array.from(bytes))))
}
